import { Component, computed, signal } from '@angular/core';
import { CheckboxChangeEventDetail, ModalController, Platform } from '@ionic/angular';
import { IonCheckboxCustomEvent } from '@ionic/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { DATE_FORMATS } from 'src/app/helpers/date/date-formats.constants';
import { DateUtils } from 'src/app/helpers/date/dateUtils';
import { DriverAcceptanceOperationType, SaveDriverAcceptanceRequest, VehicleOrderPolicyContentResponse } from 'src/app/models/vehicle-order-policy-response.model';
import { VehicleOrderProvider } from 'src/app/services/providers/vehicle-order-provider/vehicle-order-provider';
import { VehicleOrderInfoService } from 'src/app/services/vehicle-order-info/vehicle-order-info.service';

@Component({
  selector: 'app-vehicle-order-information',
  templateUrl: './vehicle-order-information.component.html',
  styleUrls: ['./vehicle-order-information.component.scss'],
  standalone: false
})
export class VehicleOrderInformationComponent {
  readonly backButtonSub = signal<Subscription | null>(null);
  readonly voiList = signal<VehicleOrderPolicyContentResponse[]>([]);
  readonly showValidationError = signal(false);
  readonly loadFailed = signal(false);
  readonly isChecked = computed(() => {
    const list = this.voiList();
    return list.some(item => item.driverAcceptance && !item.vehicleOrderUpdated)
      && !list.some(item => item.vehicleOrderUpdated);
  });
  readonly isCheckboxEnabled = computed(() =>
    this.voiList().every(item => item.checkboxOnOff)
  );
  readonly showDriverValidationMessage = computed(() => this.voiList().some(
    item => item.driverAcceptance && item.vehicleOrderUpdated
  ));
  private get driverAcceptanceUpdateMessage(): string {
    const messageKey = 'VALIDATION_MESSAGE_DRIVER_ACCEPTANCE_UPDATE';
    const noDateMessageKey = 'VALIDATION_MESSAGE_DRIVER_ACCEPTANCE_UPDATE_NO_DATE';
    const updateDate = this.getFormattedUpdatedStartDate();
    if (!updateDate) {
      return this.translateService.instant(noDateMessageKey);
    }

    return this.translateService.instant(messageKey, {
      updateDate,
      startDate: updateDate
    });
  }
  readonly showCheckBox = computed(() => this.voiList().some(
    item => item.showAcknowledgement
  ));

  constructor(
    private readonly modalCtrl: ModalController,
    private readonly platform: Platform,
    private readonly vehicleOrderInfoService: VehicleOrderInfoService,
    private readonly translateService: TranslateService,
    private readonly vehicleOrderProvider: VehicleOrderProvider) { }

  close() {
    if (!this.canClose()) {
      this.showError();
      return;
    }

    this.modalCtrl.dismiss({ accepted: true });
  }

  ionViewWillEnter() {
    this.loadFailed.set(false);
    if (this.vehicleOrderProvider.isPolicyContentLoaded()) {
      this.voiList.set(this.vehicleOrderProvider.policyContent() ?? []);
    } else {
      this.vehicleOrderInfoService.getVehicleOrderPolicyContent().subscribe({
        next: (content) => {
          this.vehicleOrderProvider.setPolicyContent(content);
          this.voiList.set(content ?? []);
        },
        error: () => {
          this.voiList.set([]);
          this.loadFailed.set(true);
        }
      });
    }

    this.backButtonSub.set(this.platform.backButton.subscribeWithPriority(9999, () => {
      if (this.canClose()) {
        this.modalCtrl.dismiss({ accepted: true });
      } else {
        this.showError();
      }
    }));
    history.pushState(null, '', location.href);
    globalThis.addEventListener('popstate', this.popStateListener);
  }

  ionViewWillLeave() {
    this.backButtonSub()?.unsubscribe();
    this.backButtonSub.set(null);
    globalThis.removeEventListener('popstate', this.popStateListener);
  }
  onCheckboxChange(event: IonCheckboxCustomEvent<CheckboxChangeEventDetail<boolean>>) {
    if (!event.detail.checked) { return; }

    const opType = this.getOperationType();
    if (!opType) {
      this.modalCtrl.dismiss({ accepted: true });
      return;
    }

    const list = this.voiList();
    const holmanItem = list.find(item => !item.isClient);
    const clientItem = list.find(item => item.isClient);

    if (!holmanItem && !clientItem) {
      this.modalCtrl.dismiss({ accepted: true });
      return;
    }

    const payload: SaveDriverAcceptanceRequest = {
      operationType: opType,
      holmanVehicleOrderPolicyId: holmanItem?.showAcknowledgement ? holmanItem.vehicleOrderingPolicyId : null,
      clientVehicleOrderPolicyId: clientItem?.showAcknowledgement ? clientItem.vehicleOrderingPolicyId : null
    };
    this.vehicleOrderInfoService.saveDriverAcceptance(payload).subscribe({
      next: () => {
        this.vehicleOrderProvider.clearPolicy();
        this.modalCtrl.dismiss({ accepted: true });
      },
      error: () => {
        this.showValidationError.set(true);
      }
    });
  }

  canClose(): boolean {
    if (this.loadFailed()) return true;
    if (this.voiList().length === 0) return true;
    return this.isChecked() || !this.isCheckboxEnabled();
  }

  showError() {
    this.showValidationError.set(true);
  }

  getDriverAcceptanceUpdateMessage(): string {
    return this.driverAcceptanceUpdateMessage;
  }

  private getOperationType(): DriverAcceptanceOperationType | null {
    const list = this.voiList();

    if (list.some(item => item.vehicleOrderUpdated)) {
      return 'Update';
    }

    if (list.some(item => !item.driverAcceptance)) {
      return 'Insert';
    }

    return null;
  }

  private readonly popStateListener = () => {
    if (this.canClose()) {
      this.modalCtrl.dismiss({ accepted: true });
    } else {
      this.showError();
      history.pushState(null, '', location.href);
    }
  };

  private getFormattedUpdatedStartDate(): string | null {
    const updatedStartDate = this.voiList()
      .filter(item => item.driverAcceptance && item.vehicleOrderUpdated)
      .map(item => item.startDateUTC)
      .find((date): date is string => Boolean(date));

    if (!updatedStartDate) {
      return null;
    }

    return DateUtils.format(updatedStartDate, false, DATE_FORMATS.DATE_DD_MM_YYYY);
  }
}
